// Diálogos de cadastro e edição da credencial. Ficam fora da aba porque o formulário é a parte
// mais densa da tela — e porque criar credencial é o ponto onde um engano custa caro: um "Criar"
// distraído sobre estado antigo gera acesso de parceiro duplicado e vivo.
import { zodResolver } from "@hookform/resolvers/zod"
import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import type { AppCredential } from "@/types/app-credential"

import { previewClientId } from "../app-credential-client-id"
import type { AppCredentialFormValues } from "../schemas"
import {
  buildAppCredentialFormSchema,
  DEFAULT_EXPIRATION_SECONDS,
  MAX_EXPIRATION_SECONDS,
  MIN_EXPIRATION_SECONDS,
} from "../schemas"

const panelPrefix = "pages.adminWhiteLabels.appCredentials"

/**
 * Atalhos de validade do token. Cobrem a faixa aceita pelo gateway (60s a 24h) e incluem o
 * default de 2h, para que ao abrir o diálogo um atalho já apareça ativo em vez de o operador ver
 * só um número solto no campo.
 */
const EXPIRATION_PRESETS = [
  { key: "fiveMinutes", seconds: 300 },
  { key: "oneHour", seconds: 3600 },
  { key: "twoHours", seconds: 7200 },
  { key: "threeHours", seconds: 10800 },
  { key: "twelveHours", seconds: 43200 },
  { key: "oneDay", seconds: 86400 },
] as const

const useCredentialForm = (
  defaultValues: AppCredentialFormValues
) => {
  const { t } = useTranslation()

  const schema = useMemo(
    () =>
      buildAppCredentialFormSchema({
        nameRequired: t(
          `${panelPrefix}.form.errors.nameRequired`
        ),
        nameTooLong: t(
          `${panelPrefix}.form.errors.nameTooLong`
        ),
        nameInvalid: t(
          `${panelPrefix}.form.errors.nameInvalid`
        ),
        expirationRange: t(
          `${panelPrefix}.form.errors.expirationRange`,
          {
            min: MIN_EXPIRATION_SECONDS,
            max: MAX_EXPIRATION_SECONDS,
          }
        ),
      }),
    [t]
  )

  return useForm<AppCredentialFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: "onBlur",
  })
}

interface CredentialFieldsProps {
  form: ReturnType<typeof useCredentialForm>
  /** O clientId é derivado do nome só no cadastro; na edição ele é imutável. */
  showClientIdPreview?: boolean
}

const CredentialFields = ({
  form,
  showClientIdPreview = false,
}: CredentialFieldsProps) => {
  const { t } = useTranslation()
  const name = form.watch("name")
  const expiration = form.watch("expirationTokenTime")
  const clientIdPreview = previewClientId(name ?? "")

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t(`${panelPrefix}.form.name`)}
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder={t(
                  `${panelPrefix}.form.namePlaceholder`
                )}
              />
            </FormControl>
            {showClientIdPreview && clientIdPreview ? (
              <FormDescription>
                {t(`${panelPrefix}.form.clientIdPreview`)}{" "}
                <span className="font-mono font-medium text-foreground">
                  {clientIdPreview}
                </span>
              </FormDescription>
            ) : (
              <FormDescription>
                {t(`${panelPrefix}.form.nameHint`)}
              </FormDescription>
            )}
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="expirationTokenTime"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {t(`${panelPrefix}.form.expiration`)}
            </FormLabel>

            <div className="flex flex-wrap gap-1.5">
              {EXPIRATION_PRESETS.map((preset) => {
                const isActive =
                  Number(expiration) === preset.seconds
                return (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() =>
                      field.onChange(preset.seconds)
                    }
                    aria-pressed={isActive}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                      isActive
                        ? "border-blue-200 bg-blue-50 text-blue-700"
                        : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                    )}
                  >
                    {t(
                      `${panelPrefix}.form.expirationPresets.${preset.key}`
                    )}
                  </button>
                )
              })}
            </div>

            <FormControl>
              <Input
                type="number"
                min={MIN_EXPIRATION_SECONDS}
                max={MAX_EXPIRATION_SECONDS}
                {...field}
              />
            </FormControl>
            <FormDescription>
              {t(`${panelPrefix}.form.expirationHint`, {
                min: MIN_EXPIRATION_SECONDS,
                max: MAX_EXPIRATION_SECONDS,
              })}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  )
}

interface CreateDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  isPending: boolean
  onSubmit: (values: AppCredentialFormValues) => void
}

export const CreateCredentialDialog = ({
  isOpen,
  onOpenChange,
  isPending,
  onSubmit,
}: CreateDialogProps) => {
  const { t } = useTranslation()
  const form = useCredentialForm({
    name: "",
    expirationTokenTime: DEFAULT_EXPIRATION_SECONDS,
  })

  // Reabrir SEMPRE parte do zero. Sem isto, o nome da credencial anterior continua no campo e um
  // "Criar" distraído cadastra uma segunda credencial viva para o mesmo parceiro.
  //
  // A reinicialização observa a transição de `isOpen` — e não o `onOpenChange` — porque quem abre
  // é o pai, e nesse caminho o Radix não dispara o callback.
  const [wasOpen, setWasOpen] = useState(isOpen)
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen)
    if (isOpen) {
      form.reset({
        name: "",
        expirationTokenTime: DEFAULT_EXPIRATION_SECONDS,
      })
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(`${panelPrefix}.createTitle`)}
          </DialogTitle>
          <DialogDescription>
            {t(`${panelPrefix}.createDescription`)}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
          >
            <CredentialFields
              form={form}
              showClientIdPreview
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isPending}>
                {t("common.create")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

interface EditDialogProps {
  credential?: AppCredential
  onOpenChange: (open: boolean) => void
  isPending: boolean
  onSubmit: (values: AppCredentialFormValues) => void
}

export const EditCredentialDialog = ({
  credential,
  onOpenChange,
  isPending,
  onSubmit,
}: EditDialogProps) => {
  const { t } = useTranslation()
  const form = useCredentialForm({
    name: credential?.name ?? "",
    expirationTokenTime:
      credential?.expirationTokenTime ??
      DEFAULT_EXPIRATION_SECONDS,
  })

  // Trocar de credencial sem fechar o diálogo tem que recarregar os valores — senão a edição de
  // uma grava o nome da outra. Padrão "adjust state during render", sem useEffect.
  const [lastUuid, setLastUuid] = useState(credential?.uuid)
  if (credential?.uuid !== lastUuid) {
    setLastUuid(credential?.uuid)
    form.reset({
      name: credential?.name ?? "",
      expirationTokenTime:
        credential?.expirationTokenTime ??
        DEFAULT_EXPIRATION_SECONDS,
    })
  }

  return (
    <Dialog
      open={Boolean(credential)}
      onOpenChange={onOpenChange}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(`${panelPrefix}.editTitle`)}
          </DialogTitle>
          <DialogDescription>
            {t(`${panelPrefix}.editDescription`, {
              clientId: credential?.clientId ?? "",
            })}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
          >
            <CredentialFields form={form} />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isPending}>
                {t("common.save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
